import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loading } from '@framework/ui/atoms/Loading';
import { LoginMethodMenu } from './LoginMethodMenu';
import { useLoginErrorMessage } from './useLoginErrorMessage';
import { LoginWithEmailFormContainer } from './LoginWithEmailFormContainer';
import { useFirebaseAuthRedirectWithGoogle } from './useFirebaseAuthRedirectWithGoogle';
import { useFirebaseAuthRedirectWithMicrosoft } from './useFirebaseAuthRedirectWithMicrosoft';
import { toast } from 'react-hot-toast';

export const LoginForm: React.FC = () => {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);

  const firebaseAuthRedirectWithGoogle = useFirebaseAuthRedirectWithGoogle();
  const firebaseAuthRedirectWithMicrosoft = useFirebaseAuthRedirectWithMicrosoft();

  const handleLoginWithGoogle = useCallback(async () => {
    setProcessing(true);

    try {
      // redirect を跨いで requestPath/signUpMode を保存してから Google 認証へ遷移する。
      // 成功時はページが redirect で離脱するため processing は戻さない。
      await firebaseAuthRedirectWithGoogle({ withInvitation: false });
    } catch (e) {
      console.error(e);
      toast.error(t('auth.login.processingError'));
      setProcessing(false);
    }
  }, [firebaseAuthRedirectWithGoogle, t]);

  const handleLoginWithMicrosoft = useCallback(async () => {
    setProcessing(true);

    try {
      // redirect を跨いで requestPath/signUpMode を保存してから Microsoft 認証へ遷移する。
      // 成功時はページが redirect で離脱するため processing は戻さない。
      await firebaseAuthRedirectWithMicrosoft({ withInvitation: false });
    } catch (e) {
      console.error(e);
      toast.error(t('auth.login.processingError'));
      setProcessing(false);
    }
  }, [firebaseAuthRedirectWithMicrosoft, t]);

  const [showLoginWithEmailForm, setShowLoginWithEmailForm] = useState<boolean>(false);
  const handleLoginWithEmail = useCallback(() => setShowLoginWithEmailForm(true), []);
  const handleCloseLoginWithEmailForm = useCallback(() => setShowLoginWithEmailForm(false), []);

  const loginErrorMessage = useLoginErrorMessage();

  if (showLoginWithEmailForm) {
    return <LoginWithEmailFormContainer onClose={handleCloseLoginWithEmailForm} />;
  }

  if (processing) {
    return (
      <div className="flex w-80 flex-col space-y-4 px-5 py-2">
        <Loading message={t('auth.login.processing')} />
      </div>
    );
  }

  return (
    <>
      <LoginMethodMenu
        onLoginWithGoogle={handleLoginWithGoogle}
        onLoginWithMicrosoft={handleLoginWithMicrosoft}
        onLoginWithEmail={handleLoginWithEmail}
      />

      {loginErrorMessage && <div className="w-1/2 min-w-80 text-red-700">{loginErrorMessage}</div>}
    </>
  );
};
